import { createAsyncThunk } from "@reduxjs/toolkit";
import ProductApiService from "../apiServices/ProductApiService";
import genericUtility from "../utility/GenericUtility";
import { Query, ProductDetailsModal } from "../../interfaces/ProductObject";

export const loadProductDetailsById = createAsyncThunk(
  "productDetails/loadById",
  async (productId: string) => {
    // Details-page read: also pull an Info listing's page body + sponsor.
    const response: any = await ProductApiService.getProductsByIds([productId], { includeInfoContent: true });
    const products = response.contents[0].products;
    if (!products || products.length === 0) {
      throw new Error("Product not found");
    }
    const dict = genericUtility.listToDictionary(products);
    return dict;
  },
);

export const loadPublicProductDetailsById = createAsyncThunk(
  "productDetails/loadPublicById",
  async (productId: string) => {
    // getProductDetailsById now unwraps the response itself — see its doc
    // comment — so this is already a real product, not the wrapper envelope.
    const productDetails = await ProductApiService.getProductDetailsById(productId);

    // Convert single product to dictionary format for consistency
    const dict = { [productId]: productDetails };
    return dict;
  },
);

export const loadProductDetailsByIds = createAsyncThunk(
  "productDetails/loadByIds",
  async (productIds: string[]) => {
    // Only the details page (logged-out path) dispatches this thunk.
    const response: any = await ProductApiService.getProductsByIds(productIds, { includeInfoContent: true });
    const dict = genericUtility.listToDictionary(response.contents[0].products);
    return dict;
  },
);

export const loadProductDetailsByUserId = createAsyncThunk(
  "productDetails/loadByUserId",
  async (userId: string) => {
    const response: any = await ProductApiService.getProductsByUserIds(userId);
    const dict = genericUtility.listToDictionary(response.contents[0].products);
    return dict;
  },
);

export const postAskQuery = createAsyncThunk(
  "productDetails/postAskQuery",
  async (
    payload: { productId: string; buyerId: string; question: string },
    { dispatch },
  ) => {
    const result = await ProductApiService.postAskQuery({
      productId: payload.productId,
      buyerId: payload.buyerId,
      question: payload.question,
    });
    dispatch(loadProductDetailsById(payload.productId));
    return { productId: payload.productId, query: result as Query };
  },
);

export const postAnswerQuery = createAsyncThunk(
  "productDetails/postAnswerQuery",
  async (
    payload: { productId: string; queryId: string; answer: string },
    { dispatch },
  ) => {
    const result = await ProductApiService.postAnswerQuery({
      productId: payload.productId,
      queryId: payload.queryId,
      answer: payload.answer,
    });
    dispatch(loadProductDetailsById(payload.productId));
    // Backend may return empty contents on successful update; don't append
    return {
      productId: payload.productId,
      query: undefined as unknown as Query,
    };
  },
);

const ProductDetailsThunks = {
  loadProductDetailsById,
  loadPublicProductDetailsById,
  loadProductDetailsByIds,
  loadProductDetailsByUserId,
  postAskQuery,
  postAnswerQuery,
};

export default ProductDetailsThunks;
